import { toEpochMs, toPlotTime } from './trendResolutionUtils';

export const CONFIGURED_LINE_COLOR = 'rgb(220, 20, 20)';
export const TRANSIENT_LEGEND_COLOR = 'rgb(255, 140, 0)'; // Dark orange for legend

// Absolute transient severity thresholds (meters). Used as a fallback only —
// live values come from the project's pressure settings endpoint.
export const DEFAULT_TRANSIENT_THRESHOLDS = { med: 20, high: 30 };

// Discrete 3-tier severity scale: low (light yellow) -> medium (dark orange) -> high (red).
export const TRANSIENT_SEVERITY_COLORS = {
  low: 'rgb(255, 240, 130)',
  medium: 'rgb(255, 135, 0)',
  high: 'rgb(255, 0, 0)',
};

/**
 * Classify a transient by its absolute height (max_reading - min_reading)
 * against the project's severity thresholds.
 *
 * @param {number} height     - transient height in meters
 * @param {Object} thresholds - { med, high } absolute thresholds in meters
 * @returns {'low'|'medium'|'high'}
 */
export const getTransientSeverity = (height, thresholds = DEFAULT_TRANSIENT_THRESHOLDS) => {
  const { med, high } = thresholds || DEFAULT_TRANSIENT_THRESHOLDS;
  if (height >= high) return 'high';
  if (height >= med) return 'medium';
  return 'low';
};

export const getTransientColor = (height, thresholds) =>
  TRANSIENT_SEVERITY_COLORS[getTransientSeverity(height, thresholds)];

export const createConfiguredLine = (dates, value, name) => ({
  x: [dates[0], dates[dates.length - 1]],
  y: [value, value],
  mode: 'lines',
  type: 'scatter',
  name,
  line: { color: CONFIGURED_LINE_COLOR, width: 2.5, dash: 'dash' },
  showlegend: true,
  hoverinfo: 'y+name',
});

/**
 * Build an array of Plotly trace objects for pressure data.
 *
 * @param {Object} params
 * @param {Array}  params.readingData       - Array of { time_utc, measurement_m }
 * @param {string} params.timeZone          - global time zone mode ('local' / 'gmt' / 'project')
 * @param {string} params.projectTimezone   - IANA name of the project's time zone, used when mode is 'project'
 * @param {Array}  params.pressureAlarms    - Array of { alarm_time, value_m }
 * @param {Array}  params.transients        - Array of { event_time, min_reading, max_reading }
 * @param {Object} params.lastPressureSample - { configured_max_m, configured_min_m }
 * @param {number} params.fromTime          - start of range (ms since epoch)
 * @param {number} params.toTime            - end of range (ms since epoch)
 * @param {Object} params.transientThresholds - { med, high } absolute severity thresholds (meters)
 * @param {Function} params.t               - i18n translate function
 * @returns {Array} Plotly trace objects
 */
export const buildPressureTraces = ({ readingData, timeZone, projectTimezone, pressureAlarms, transients, lastPressureSample, fromTime, toTime, transientThresholds, t }) => {
  if (!readingData?.length) return [];

  // Timezone-naive axis strings, like the overlay graph: Plotly renders them
  // literally, while Date objects get re-anchored to the browser's timezone
  // (WEB-1218). Also keeps full second/ms precision, which the old
  // DateType.DATE_AND_TIME path truncated to whole minutes.
  const xValues = readingData.map(item => toPlotTime(item.time_utc, timeZone, projectTimezone));
  const yValues = readingData.map(item => item.measurement_m);

  const traces = [{
    x: xValues,
    y: yValues,
    mode: 'lines',
    type: 'scatter',
    name: 'Pressure Reading',
  }];

  if (lastPressureSample) {
    if (lastPressureSample.configured_max_m) {
      traces.push(createConfiguredLine(xValues, lastPressureSample.configured_max_m, t('trend_max')));
    }
    if (lastPressureSample.configured_min_m) {
      traces.push(createConfiguredLine(xValues, lastPressureSample.configured_min_m, t('trend_min')));
    }
  }

  // Transients as vertical lines, colored by absolute severity thresholds
  if (transients?.length) {
    const transientsInRange = transients.filter(tr => {
      const time = toEpochMs(tr.event_time);
      return time >= fromTime && time <= toTime;
    });

    if (transientsInRange.length) {
      const transientData = transientsInRange
        .map(item => ({
          ...item,
          minVal: parseFloat(item.min_reading),
          maxVal: parseFloat(item.max_reading),
          height: parseFloat(item.max_reading) - parseFloat(item.min_reading),
        }))
        .filter(item => !isNaN(item.minVal) && !isNaN(item.maxVal));

      // Add legend entry for transients (first one shows in legend)
      let isFirstTransient = true;
      transientData.forEach((item) => {
        const severity = getTransientSeverity(item.height, transientThresholds);
        const color = TRANSIENT_SEVERITY_COLORS[severity];
        const date = toPlotTime(item.event_time, timeZone, projectTimezone);

        traces.push({
          x: [date, date],
          y: [item.minVal, item.maxVal],
          mode: 'lines',
          type: 'scatter',
          name: 'Transients',
          line: { color: isFirstTransient ? TRANSIENT_LEGEND_COLOR : color, width: 3 },
          showlegend: isFirstTransient,
          legendgroup: 'transients',
          hoverinfo: 'text',
          text: [`Min: ${item.minVal.toFixed(2)}, Max: ${item.maxVal.toFixed(2)}, Severity: ${severity}`],
        });
        isFirstTransient = false;
      });
    }
  }

  // Alarms as red dots
  if (pressureAlarms?.length) {
    const alarmsInRange = pressureAlarms.filter(a => {
      const time = toEpochMs(a.alarm_time);
      return time >= fromTime && time <= toTime;
    });

    if (alarmsInRange.length) {
      traces.push({
        x: alarmsInRange.map(a => toPlotTime(a.alarm_time, timeZone, projectTimezone)),
        y: alarmsInRange.map(a => a.value_m),
        mode: 'markers',
        type: 'scatter',
        name: 'Alarms',
        marker: { color: 'red', size: 12, symbol: 'circle' },
      });
    }
  }

  return traces;
};
